import { GraphiQL } from "graphiql";
import { useGraphiQL } from "@graphiql/react";
import { useOutletContext } from "react-router-dom";
import { useEffect, useRef, useState } from 'react';
import {
    APIMajorVersions,
    BackEndEnvironments,
    BackEndEnvironmentUrls,
    BackEndEnvironmentWebSockets,
    LATEST_V1_MINOR_VERSION,
    LATEST_V2_MINOR_VERSION,
    PAGE_TITLE_BASE,
    PageDisplayNames,
    PageNames,
    WebSocketSchemaUrls
} from '../../Assets/variables';
import GraphqlExplorerHeader from "../../Components/GraphQLExplorerHeader";
import { graphiQLQueryExamples } from "../../Assets/exampleGraphiQLQuery";
import { backEndEnvironmentToUse } from "../../Utils/environmentHelpers";
import { createGraphiQLFetcher } from '@graphiql/toolkit';
import { Helmet } from "react-helmet-async";
import 'graphiql/setup-workers/webpack';
import 'graphiql/style.css'

const Version1INT = {
    URL: `${BackEndEnvironmentUrls.QA}/web/v1/graphql?`,
    Websocket: `${BackEndEnvironmentWebSockets.QA}/web/v1/websocket?`,
    Schema: WebSocketSchemaUrls.V1,
    ApiVersion: LATEST_V1_MINOR_VERSION
};

const Version1PREPROD = {
    URL: `${BackEndEnvironmentUrls.PREPROD}/web/v1/graphql?`,
    Websocket: `${BackEndEnvironmentWebSockets.PREPROD}/web/v1/websocket?`,
    Schema: WebSocketSchemaUrls.V1,
    ApiVersion: LATEST_V1_MINOR_VERSION
};

const Version1PROD = {
    URL: `${BackEndEnvironmentUrls.PROD}/web/v1/graphql`,
    Websocket: `${BackEndEnvironmentWebSockets.PROD}/web/v1/websocket`,
    Schema: WebSocketSchemaUrls.V1,
    ApiVersion: LATEST_V1_MINOR_VERSION
};

const Version2INT = {
    URL: `${BackEndEnvironmentUrls.QA}/web/v2/graphql?`,
    Websocket: `${BackEndEnvironmentWebSockets.QA}/web/v2/websocket?`,
    Schema: WebSocketSchemaUrls.V2,
    ApiVersion: LATEST_V2_MINOR_VERSION
};

const Version2PREPROD = {
    URL: `${BackEndEnvironmentUrls.PREPROD}/web/v2/graphql?`,
    Websocket: `${BackEndEnvironmentWebSockets.PREPROD}/web/v2/websocket?`,
    Schema: WebSocketSchemaUrls.V2,
    ApiVersion: LATEST_V2_MINOR_VERSION
};

const Version2PROD = {
    URL: `${BackEndEnvironmentUrls.PROD}/web/v2/graphql`,
    Websocket: `${BackEndEnvironmentWebSockets.PROD}/web/v2/websocket`,
    Schema: WebSocketSchemaUrls.V2,
    ApiVersion: LATEST_V2_MINOR_VERSION
};

const ExampleQuerySync = ({query}) => {
    const queryEditor = useGraphiQL(state => state.queryEditor);

    useEffect(() => {
        queryEditor?.setValue(query);
    }, [query, queryEditor]);

    return null;
};

const GraphqlExplorer = () => {
    const {sidebarToggler, setActivePageItem} = useOutletContext();
    const exampleQuerySelectRef = useRef("Select Example Query");
    const versionSelectRef = useRef(APIMajorVersions.v2);
    const [selectedQuery, setSelectedQuery] = useState(getSelectedQuery("Select Example Query"));
    const [selectedVersion, setSelectedVersion] = useState(Version2PREPROD);

    function getSelectedQuery(selectedQueryName) {
        return graphiQLQueryExamples.filter(query => query.displayName === selectedQueryName)[0].query;
    }

    function updateBasedOnSelectedVersion() {
        if (backEndEnvironmentToUse() === BackEndEnvironments.QA) {
            return versionSelectRef.current.value === APIMajorVersions.v1 ? setSelectedVersion(Version1INT) : setSelectedVersion(Version2INT);
        } else if (backEndEnvironmentToUse() === BackEndEnvironments.PREPROD) {
            return versionSelectRef.current.value === APIMajorVersions.v1 ? setSelectedVersion(Version1PREPROD) : setSelectedVersion(Version2PREPROD);
        } else {
            return versionSelectRef.current.value === APIMajorVersions.v1 ? setSelectedVersion(Version1PROD) : setSelectedVersion(Version2PROD);
        }
    }

    function updateBasedOnSelectedQuery() {
        setSelectedQuery(getSelectedQuery(exampleQuerySelectRef.current.value));
    }

    const fetcher = createGraphiQLFetcher({
        url: selectedVersion.URL,
        method: "post",
        headers: {
            Accept: 'application/json',
            'content-type': 'application/json',
            'api-version': selectedVersion.ApiVersion,
            'Accept-Encoding': 'gzip',
        },
        wsClient: {
            on: function (e_1) {
                console.log(`on ${e_1}`);
            },
            subscribe: function (payload_1, sink) {
                let webSocket = new WebSocket(selectedVersion.Websocket);

                webSocket.onopen = function () {
                    console.log("opening websocket");
                    let json = JSON.stringify(payload_1);
                    console.log(`sending ${payload_1}`);
                    webSocket.send(json);
                };

                webSocket.onmessage = function (event) {
                    console.log("onmessage event.data: ", event.data);
                    sink.next(JSON.parse(event.data));
                };

                webSocket.onerror = function (event_1) {
                    console.log("onerror message: ", event_1);
                    sink.next(event_1);
                };

                webSocket.onclose = function () {
                    console.log("closing websocket");
                };

                return () => webSocket.close();
            }
        }
    });
    
    useEffect(() => {
        sidebarToggler(false);
        setActivePageItem(PageNames.GraphQLExplorer);
        updateBasedOnSelectedVersion();
    }, []);
    
    return (
        <>
            <Helmet>
                <title>{`${PAGE_TITLE_BASE}${PageDisplayNames.GraphQLExplorer}`}</title>
                <meta name="description" content={`${PageDisplayNames.GraphQLExplorer} page of the SIX Web API Portal`} />
                <link rel="canonical" href={`/${PageNames.GraphQLExplorer}`} />
            </Helmet>
            <GraphqlExplorerHeader specificationLink={selectedVersion.Schema} />
            <div className="selections">
                <div className="example-query">
                    <select name="example-query" id="example-query__select" className="six-dropdown" ref={exampleQuerySelectRef} onChange={(event) => {updateBasedOnSelectedQuery()}}>
                        { graphiQLQueryExamples.map(graphiQLQueryExample => (
                            <option key={graphiQLQueryExample.displayName}>{graphiQLQueryExample.displayName}</option>
                        )) }
                    </select>
                </div>
                <select name="version" id="selector-version" className="six-dropdown" ref={versionSelectRef} onChange={(event) => {updateBasedOnSelectedVersion()}}>
                    <option value={APIMajorVersions.v2} defaultValue selected>{`Version ${APIMajorVersions.v2}`}</option>
                    <option value={APIMajorVersions.v1}>{`Version ${APIMajorVersions.v1}`}</option>
                </select>
            </div>
            <div id="wrapper">
                <GraphiQL
                    fetcher={fetcher}
                    defaultQuery={selectedQuery}
                    initialQuery={selectedQuery}
                    defaultTheme={"light"}
                    defaultEditorToolsVisibility={false}
                >
                    <ExampleQuerySync query={selectedQuery} />
                </GraphiQL>
            </div>
        </>
    );
};

export default GraphqlExplorer;